import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import type { ApiValue } from '@goodneighbor/analytics';
import { analytics } from '@/services/analytics';

/**
 * useEnrichedPageView - Track page views with additional context
 *
 * Use this hook when you need to track page views with enriched data
 * such as organization IDs, event filters, user segments, etc.
 *
 * This supplements the automatic page view tracking by sending an
 * additional track event with the enriched context.
 *
 * @param eventName - Name of the track event (e.g., "Organization Page Viewed", "Event Filters Applied")
 * @param properties - Additional context properties
 *
 * @example
 * ```tsx
 * useEnrichedPageView('Organization Page Viewed', {
 *   organizationId: org.id,
 *   organizationName: org.name,
 *   organizationSlug: org.slug
 * });
 * ```
 *
 * @example
 * ```tsx
 * useEnrichedPageView('Events Page Viewed', {
 *   filters: {
 *     category: selectedCategory,
 *     location: selectedLocation,
 *     dateRange: dateRange
 *   },
 *   resultCount: events.length
 * });
 * ```
 */
export function useEnrichedPageView(
  eventName: string,
  properties: {
    organizationId?: string;
    eventId?: string;
    filters?: Record<string, ApiValue>;
    [key: string]: ApiValue;
  },
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled ?? true;
  const pathname = usePathname();

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    analytics
      .initialize()
      .then(() => {
        if (cancelled) return;

        // Track enriched page view with additional context
        analytics.enrichedPageView(eventName, {
          path: pathname,
          url: window.location.href,
          title: document.title,
          referrer: document.referrer,
          ...properties
        });
      })
      .catch((error) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(
          `[useEnrichedPageView] Analytics initialization failed:`,
          error
        );
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, pathname, JSON.stringify(properties), enabled]);
}
